import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";
import { useUpdateFirm } from "@/lib/data/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useUser } from "@/components/UserProvider";

export const STEP_IDS = [
  "welcome",
  "profile-photos",
  "matter-status",
  "matter-types",
  "matter-expenses",
  "task-plans",
] as const;

export const StepIdSchema = z.enum(STEP_IDS);
export type StepIdType = z.infer<typeof StepIdSchema>;

export type OnboardingStep = {
  id: StepIdType;
  label: string;
  heading?: string;
  description?: ReactNode;
  nextLabel?: string;
  Component: () => ReactNode;
};

type OnboardingStepContextValue = {
  steps: OnboardingStep[];
  activeStep: OnboardingStep;
  activeStepId: StepIdType;
  activeStepIndex: number;
  stepCount: number;
  isFirst: boolean;
  isLast: boolean;
  isCompleting: boolean;
  setActiveStepId: (stepId: StepIdType) => void;
  goNext: () => void;
  goPrevious: () => void;
};

const OnboardingStepContext = createContext<OnboardingStepContextValue | null>(
  null,
);

export function OnboardingStepProvider({
  steps,
  initialStepId,
  children,
}: {
  steps: OnboardingStep[];
  initialStepId?: StepIdType;
  children: ReactNode;
}) {
  if (steps.length === 0) {
    throw new Error("OnboardingStepProvider requires at least one step.");
  }

  const initialId = initialStepId ?? steps[0].id;
  const [activeStepId, setActiveStepIdState] = useState<StepIdType>(initialId);
  const [isCompleting, setIsCompleting] = useState(false);
  const hasUserSetStepRef = useRef(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { currentUser } = useUser();
  const updateFirm = useUpdateFirm({ showToast: false });
  const firm = currentUser.firm;
  const firmRef = useRef(firm);
  const navigate = useNavigate({ from: "/onboarding" });

  const activeStepIndex = useMemo(() => {
    const index = steps.findIndex((step) => step.id === activeStepId);
    return index === -1 ? 0 : index;
  }, [activeStepId, steps]);

  const activeStep = steps[activeStepIndex];

  useEffect(() => {
    const onboardingProgress = firm.onboardingProgress;
    if (!onboardingProgress || onboardingProgress === "COMPLETED") return;
    if (hasUserSetStepRef.current) return;

    const parsedStepId = StepIdSchema.safeParse(onboardingProgress);
    if (!parsedStepId.success) return;
    const stepId = parsedStepId.data;
    if (!steps.some((step) => step.id === stepId)) return;

    if (stepId === activeStepId) return;

    setActiveStepIdState(stepId);
  }, [activeStepId, firm, steps]);

  useEffect(() => {
    firmRef.current = firm;
  }, [firm]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const persistStepProgress = useCallback(
    (stepId: StepIdType) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        const currentFirm = firmRef.current;
        if (currentFirm.onboardingProgress === "COMPLETED") return;
        if (currentFirm.onboardingProgress === stepId) return;

        const payload = {
          name: currentFirm.name,
          dateModified: currentFirm.dateModified,
          onboardingProgress: stepId,
        };

        void updateFirm.mutateAsync(payload).catch(() => {});
      }, 1000);
    },
    [updateFirm],
  );

  const updateActiveStepId = useCallback(
    (stepId: StepIdType) => {
      if (!steps.some((step) => step.id === stepId)) {
        return;
      }

      setActiveStepIdState(stepId);
      hasUserSetStepRef.current = true;

      persistStepProgress(stepId);
    },
    [persistStepProgress, steps],
  );

  const setActiveStepId = updateActiveStepId;

  const goNext = useCallback(async () => {
    if (activeStepIndex >= steps.length - 1) {
      if (firm.onboardingProgress === "COMPLETED") return;
      if (isCompleting) return;

      setIsCompleting(true);
      try {
        await updateFirm.mutateAsync({
          name: firm.name,
          dateModified: firm.dateModified,
          onboardingProgress: "COMPLETED",
        });
        await navigate({ to: "/" });
      } catch {
        setIsCompleting(false);
      }
      return;
    }

    updateActiveStepId(steps[activeStepIndex + 1].id);
  }, [
    activeStepIndex,
    firm,
    isCompleting,
    navigate,
    steps,
    updateActiveStepId,
    updateFirm,
  ]);

  const goPrevious = useCallback(() => {
    if (activeStepIndex <= 0) return;
    updateActiveStepId(steps[activeStepIndex - 1].id);
  }, [activeStepIndex, steps, updateActiveStepId]);

  const value = useMemo(
    () => ({
      steps,
      activeStep,
      activeStepId,
      activeStepIndex,
      stepCount: steps.length,
      isFirst: activeStepIndex === 0,
      isLast: activeStepIndex === steps.length - 1,
      isCompleting,
      setActiveStepId,
      goNext,
      goPrevious,
    }),
    [
      activeStep,
      activeStepId,
      activeStepIndex,
      isCompleting,
      goNext,
      goPrevious,
      setActiveStepId,
      steps,
    ],
  );

  return (
    <OnboardingStepContext.Provider value={value}>
      {children}
    </OnboardingStepContext.Provider>
  );
}

export function useOnboardingSteps() {
  const context = useContext(OnboardingStepContext);
  if (!context) {
    throw new Error("useOnboardingSteps must be used within a provider.");
  }
  return context;
}

export function OnboardingStepRenderer() {
  const { activeStep } = useOnboardingSteps();
  const Step = activeStep.Component;
  return <Step />;
}
